import type { MergeField, MergeFieldKey, SequenceStep, StepChannel, TokenNode } from './types';

/** The palette. A field can only be inserted from here, so it cannot be mistyped. */
export const MERGE_FIELDS: MergeField[] = [
  { key: 'patient_first_name', label: 'Patient first name' },
  { key: 'appointment_date', label: 'Appointment date' },
  { key: 'appointment_time', label: 'Appointment time' },
  { key: 'provider', label: 'Provider' },
  { key: 'practice_name', label: 'Practice name' },
  { key: 'booking_link', label: 'Booking link' },
  { key: 'balance_amount', label: 'Balance amount' },
  { key: 'procedure', label: 'Procedure' },
  { key: 'plan_link', label: 'Plan link' },
];

export const MERGE_FIELD_LABEL: Record<MergeFieldKey, string> = MERGE_FIELDS.reduce(
  (acc, f) => ({ ...acc, [f.key]: f.label }),
  {} as Record<MergeFieldKey, string>,
);

export const CHANNEL_LABEL: Record<StepChannel, string> = {
  call: 'AI call',
  sms: 'Text',
  email: 'Email',
};

/** Board colours for the channel chip on a rung. */
export const CHANNEL_TONE: Record<StepChannel, string> = {
  call: '#6B4FD0',
  sms: '#2E7D62',
  email: '#3B6FD4',
};

/**
 * A channel that cannot work at an offset is greyed with its reason rather than
 * hidden (§3.2). Returning the reason rather than a boolean is deliberate: the
 * row has to render why, and a boolean would leave the caller inventing wording.
 */
export function channelBlockedAt(channel: StepChannel, offset: string): string | undefined {
  const sameDay = /^morning of$/i.test(offset.trim()) || /^same day$/i.test(offset.trim());
  if (sameDay && channel === 'email') {
    return 'A patient who has not opened their email by 7am will not open it at 7:40.';
  }
  return undefined;
}

function isFieldKey(s: string): s is MergeFieldKey {
  return Object.prototype.hasOwnProperty.call(MERGE_FIELD_LABEL, s);
}

/**
 * A short helper so callers do not build token arrays by hand in JSX. Anything
 * that matches a field key becomes a chip; everything else is literal text.
 */
export function tokens(...parts: string[]): TokenNode[] {
  return parts.map((p) => (isFieldKey(p) ? { kind: 'field', field: p } : { kind: 'text', text: p }));
}

/** Plain words, for the counters and for anything that has to read a body. */
export function tokenText(nodes: TokenNode[]): string {
  return nodes.map((n) => (n.kind === 'text' ? n.text : MERGE_FIELD_LABEL[n.field])).join('');
}

/** Spoken seconds at roughly 150 words a minute — voice steps show time, not characters. */
export function spokenSeconds(nodes: TokenNode[]): number {
  const words = tokenText(nodes).trim().split(/\s+/).filter(Boolean).length;
  return Math.round(words / 2.5);
}

/** A text is billed in 160-character segments, so a text step shows segments. */
export function segmentCount(nodes: TokenNode[]): number {
  return Math.max(1, Math.ceil(tokenText(nodes).length / 160));
}

/**
 * Card 01 of Appointment Reminders — the ladder as the board ships it.
 *
 * Two of these defaults are deliberate: the 20-day email only fires when the
 * appointment was booked far enough ahead to need it, and the morning-of
 * reminder ships **off**, because for most practices it arrives after the
 * patient has already left the house.
 */
export const REMINDER_STEPS: SequenceStep[] = [
  {
    id: 'rs-1',
    position: 1,
    offset: '20 d',
    channel: 'email',
    audience: 'Everyone',
    condition: {
      field: 'lead_time',
      op: 'gte',
      value: 15,
      label: 'the appointment was booked 15+ days ahead',
    },
    enabled: true,
    matchCount30d: 84,
    content: {
      mode: 'smart',
      subject: tokens('Your appointment on ', 'appointment_date'),
      body: tokens(
        'Let ',
        'patient_first_name',
        ' know their appointment with ',
        'provider',
        ' is coming up on ',
        'appointment_date',
        '. This is the first contact, so give the full picture.',
      ),
      mustAppear: ['the date and the time', 'who they are seeing'],
      fallback: tokens(
        'Hi ',
        'patient_first_name',
        ', your appointment with ',
        'provider',
        ' is on ',
        'appointment_date',
        ' at ',
        'appointment_time',
        '.',
      ),
    },
  },
  {
    id: 'rs-2',
    position: 2,
    offset: '3 d',
    channel: 'sms',
    audience: 'Everyone',
    enabled: true,
    content: {
      mode: 'smart',
      body: tokens(
        'Remind ',
        'patient_first_name',
        ' about ',
        'appointment_date',
        ' at ',
        'appointment_time',
        '. Ask them to confirm.',
      ),
      mustAppear: ['the date and the time', 'how to reschedule'],
      fallback: tokens(
        'Hi ',
        'patient_first_name',
        ', reminding you of your appointment on ',
        'appointment_date',
        ' at ',
        'appointment_time',
        '. Reply C to confirm.',
      ),
    },
  },
  {
    id: 'rs-3',
    position: 3,
    offset: '2 d',
    channel: 'call',
    audience: 'Still unconfirmed',
    condition: {
      field: 'confirmation',
      op: 'eq',
      value: 'unconfirmed',
      label: 'still unconfirmed',
    },
    enabled: true,
    matchCount30d: 31,
    content: {
      mode: 'smart',
      body: tokens(
        'Remind ',
        'patient_first_name',
        ' about their appointment on ',
        'appointment_date',
        ' at ',
        'appointment_time',
        ' with ',
        'provider',
        '. Ask them to confirm. Keep it short and warm — this is the second time we have contacted them, so do not start from the beginning.',
      ),
      mustAppear: ['the date and the time', 'who they are seeing', 'how to reschedule'],
      fallback: tokens(
        'Hi ',
        'patient_first_name',
        ', this is ',
        'practice_name',
        ' confirming your appointment on ',
        'appointment_date',
        ' at ',
        'appointment_time',
        ' with ',
        'provider',
        '. Call us to reschedule.',
      ),
      noAnswer: 'voicemail_stop',
    },
  },
  {
    id: 'rs-4',
    position: 4,
    offset: '1 d',
    channel: 'sms',
    audience: 'Still unconfirmed',
    condition: {
      field: 'confirmation',
      op: 'eq',
      value: 'unconfirmed',
      label: 'still unconfirmed',
    },
    enabled: true,
    matchCount30d: 22,
    content: {
      mode: 'smart',
      body: tokens('Last reminder for ', 'patient_first_name', ' — tomorrow at ', 'appointment_time', '.'),
      mustAppear: ['the date and the time'],
      fallback: tokens('Hi ', 'patient_first_name', ', see you tomorrow at ', 'appointment_time', '.'),
    },
  },
  {
    id: 'rs-5',
    position: 5,
    offset: 'Morning of',
    channel: 'sms',
    audience: 'Everyone',
    enabled: false,
    content: {
      mode: 'hard',
      body: tokens('See you today at ', 'appointment_time', ' — ', 'practice_name', '.'),
      mustAppear: [],
      fallback: [],
    },
  },
];
